package org.teddapp.project.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.focusable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntRect
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Popup
import androidx.compose.ui.window.PopupPositionProvider
import androidx.compose.ui.window.PopupProperties

/**
 * Une entrée du menu maison. [section] : un titre de groupe affiché avant cette entrée
 * quand il change. [leading] : un petit dessin à gauche (pastille, nuancier) à la place
 * de [icon]. [keepOpen] : le menu reste ouvert après le clic (case à cocher).
 */
class TeddMenuEntry(
    val label: String,
    val onClick: () -> Unit,
    val icon: String? = null,
    val iconColor: Color? = null,
    val leading: (@Composable () -> Unit)? = null,
    val hint: String? = null,
    val kbd: String? = null,
    val selected: Boolean = false,
    val danger: Boolean = false,
    val enabled: Boolean = true,
    val section: String? = null,
    val keepOpen: Boolean = false,
)

/**
 * Le menu de l'application : la liste s'ouvre sous son déclencheur, sans animation
 * ni ombre portée lourde (le navigateur les paye cher), avec la charte de l'application.
 * Au-delà de huit entrées, un champ de recherche apparaît en tête ; les flèches,
 * Entrée et Échap marchent partout. Les intitulés longs passent sur deux lignes au
 * lieu d'être coupés. À poser dans un `Box` qui contient aussi le déclencheur.
 */
@Composable
fun TeddMenu(
    expanded: Boolean,
    onDismiss: () -> Unit,
    entries: List<TeddMenuEntry>,
    modifier: Modifier = Modifier,
    width: Dp = 280.dp,
    searchable: Boolean = entries.size > 8,
    placeholder: String = "Chercher…",
    alignEnd: Boolean = false,
    maxHeight: Dp = 420.dp,
) {
    if (!expanded) return
    var query by remember { mutableStateOf("") }
    var active by remember { mutableIntStateOf(-1) }
    val folded = query.trim().lowercase()
    val shown = remember(entries, folded) {
        if (folded.isEmpty()) entries else entries.filter { it.label.lowercase().contains(folded) || (it.hint?.lowercase()?.contains(folded) == true) }
    }
    if (active >= shown.size) active = shown.size - 1
    val focus = remember { FocusRequester() }
    LaunchedEffect(Unit) { focus.requestFocus() }
    val listState = rememberLazyListState()
    val density = LocalDensity.current
    val gap = with(density) { 4.dp.roundToPx() }
    val provider = remember(alignEnd, gap) {
        object : PopupPositionProvider {
            override fun calculatePosition(anchorBounds: IntRect, windowSize: IntSize, layoutDirection: LayoutDirection, popupContentSize: IntSize): IntOffset {
                var x = if (alignEnd) anchorBounds.right - popupContentSize.width else anchorBounds.left
                x = x.coerceIn(gap, (windowSize.width - popupContentSize.width - gap).coerceAtLeast(gap))
                val below = anchorBounds.bottom + gap
                val y = if (below + popupContentSize.height <= windowSize.height || anchorBounds.top < windowSize.height / 2) {
                    below.coerceAtMost((windowSize.height - popupContentSize.height - gap).coerceAtLeast(gap))
                } else {
                    (anchorBounds.top - gap - popupContentSize.height).coerceAtLeast(gap)
                }
                return IntOffset(x, y)
            }
        }
    }
    fun pick(entry: TeddMenuEntry) {
        if (!entry.enabled) return
        if (!entry.keepOpen) onDismiss()
        entry.onClick()
    }
    Popup(popupPositionProvider = provider, onDismissRequest = onDismiss, properties = PopupProperties(focusable = true)) {
        val shape = RoundedCornerShape(TeddTokens.RMd)
        Column(
            modifier
                .width(width)
                .teddOverlayShadow(shape)
                .clip(shape)
                .background(TeddTokens.SurfaceOverlay)
                .border(1.dp, TeddTokens.LineStrong, shape)
                .onPreviewKeyEvent { event ->
                    if (event.type != KeyEventType.KeyDown) return@onPreviewKeyEvent false
                    when (event.key) {
                        Key.Escape -> { onDismiss(); true }
                        Key.DirectionDown -> { if (shown.isNotEmpty()) active = (active + 1).mod(shown.size); true }
                        Key.DirectionUp -> { if (shown.isNotEmpty()) active = (active - 1).mod(shown.size); true }
                        Key.Enter, Key.NumPadEnter -> { shown.getOrNull(active)?.let(::pick); true }
                        else -> false
                    }
                },
        ) {
            if (searchable) {
                Row(
                    Modifier.fillMaxWidth().height(44.dp).padding(horizontal = TeddSpace.S3),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2),
                ) {
                    TeddIcon("search", size = 16.dp, color = TeddTokens.InkMuted)
                    Box(Modifier.weight(1f), contentAlignment = Alignment.CenterStart) {
                        if (query.isEmpty()) Text(placeholder, color = TeddTokens.InkSubtle, style = TeddType.Body, maxLines = 1)
                        BasicTextField(
                            value = query,
                            onValueChange = { query = it; active = 0 },
                            modifier = Modifier.fillMaxWidth().focusRequester(focus),
                            singleLine = true,
                            textStyle = TeddType.Body.copy(color = TeddTokens.Ink),
                            cursorBrush = SolidColor(TeddTokens.Ink),
                        )
                    }
                    if (query.isNotEmpty()) Text("${shown.size}", color = TeddTokens.InkMuted, style = TeddType.Caption)
                }
                Box(Modifier.fillMaxWidth().height(1.dp).background(TeddTokens.Line))
            } else {
                // Sans champ, c'est la liste qui prend le clavier.
                Box(Modifier.size(0.dp).focusRequester(focus).focusable())
            }
            if (shown.isEmpty()) {
                Text("Rien ne correspond.", Modifier.padding(TeddSpace.S4), color = TeddTokens.InkMuted, style = TeddType.Caption)
            } else {
                LaunchedEffect(active) { if (active >= 0) listState.animateScrollToItem(active) }
                LazyColumn(Modifier.heightIn(max = maxHeight).padding(TeddSpace.S1), state = listState) {
                    shown.forEachIndexed { index, entry ->
                        val newSection = entry.section != null && (index == 0 || shown[index - 1].section != entry.section)
                        item(key = index) {
                            Column {
                                if (newSection) {
                                    Text(
                                        entry.section!!.uppercase(),
                                        Modifier.padding(start = TeddSpace.S3, top = if (index == 0) TeddSpace.S2 else TeddSpace.S3, bottom = TeddSpace.S1),
                                        color = TeddTokens.InkMuted,
                                        style = TeddType.Overline,
                                    )
                                }
                                MenuRow(entry, highlighted = index == active, onClick = { pick(entry) })
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun MenuRow(entry: TeddMenuEntry, highlighted: Boolean, onClick: () -> Unit) {
    val ink = when {
        !entry.enabled -> TeddTokens.InkSubtle
        entry.danger -> TeddTokens.Danger
        entry.selected -> TeddTokens.LimeStrong
        else -> TeddTokens.Ink
    }
    Row(
        Modifier
            .fillMaxWidth()
            .teddClickable(
                onClick = onClick,
                fill = if (highlighted) TeddTokens.LimeSoft else Color.Transparent,
                hover = TeddTokens.LimeSoft,
                enabled = entry.enabled,
            )
            .heightIn(min = 40.dp)
            .padding(horizontal = TeddSpace.S3, vertical = TeddSpace.S2),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
    ) {
        when {
            entry.leading != null -> entry.leading.invoke()
            entry.icon != null -> TeddIcon(entry.icon, size = 16.dp, color = entry.iconColor ?: if (entry.danger) TeddTokens.Danger else TeddTokens.InkMuted)
        }
        Text(
            entry.label,
            Modifier.weight(1f),
            color = ink,
            style = TeddType.Body.copy(fontSize = TeddType.Body.fontSize * 14 / 15, fontWeight = if (entry.selected) FontWeight.SemiBold else FontWeight.Normal),
            maxLines = 2,
            overflow = TextOverflow.Ellipsis,
        )
        if (entry.hint != null) Text(entry.hint, color = TeddTokens.InkMuted, style = TeddType.Caption)
        if (entry.kbd != null) TeddKbd(entry.kbd)
        if (entry.selected) TeddIcon("check", size = 14.dp, color = TeddTokens.LimeStrong)
    }
}
